/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { AppStore } from 'src/core/stores/app/AppStore';
import { FileType } from 'src/core/types/FileType';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { batchPromises } from 'src/core/utils/batchPromises';
import { getRandomNumber } from 'src/core/utils/randomValueGenerators';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { getQnaMakerCommonService } from 'src/qnaMaker/core/services/qnaMakerCommonService';
import { getQnaMakerProjectService } from 'src/qnaMaker/core/services/qnaMakerProjectService';
import { LogicalOperation } from 'src/qnaMaker/core/services/qnaMakerProjectSwaggers';
import { QnaMakerMetadataType } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerPredictedQnA } from 'src/qnaMaker/core/types/QnaMakerPredictedQnA';
import {
    qnaMakerProjectDtoToPayloadSwagger,
    qnaMakerProjectSwaggerToStore
} from 'src/qnaMaker/modules/handlers/project/qnaMakerProjectMappers';
import { QnaMakerProjectDto } from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/QnaMakerProjectCreationModal';

export type QnaMakerProjectHandlerGroup = ReturnType<typeof createQnaMakerProjectHandlerGroup>;

export const createQnaMakerProjectHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const getQnaMakerProjects = async (params?: HandlerParams) => {
        const { context } = params;
        const { tracker } = rootStore.qnaProjectStoreHouse;

        if (!context.forceRefresh && (tracker.isRunning || tracker.isCompleted)) {
            return;
        }

        try {
            tracker.setState('running');

            const swaggers = await getQnaMakerProjectService({ context }).getProjects();
            const stores = swaggers.map(qnaMakerProjectSwaggerToStore);
            rootStore.qnaProjectStoreHouse.setItems(stores);

            tracker.setState('completed');
        } catch (e) {
            tracker.setState('failed', <Error>e);
            rootStore.qnaProjectStoreHouse.setItems([]);
        }
    };

    const createQnaMakerProject = async (params?: HandlerParams<{ projectMetaData: QnaMakerProjectDto }>) => {
        const { tracker, context, projectMetaData } = params;

        if (tracker.isRunning || tracker.isCompleted) {
            return;
        }

        try {
            tracker.setState('running');

            const projectSwagger = await getQnaMakerProjectService({ context }).createOrUpdateProject({
                payload: qnaMakerProjectDtoToPayloadSwagger(projectMetaData)
            });

            const projectStore = qnaMakerProjectSwaggerToStore(projectSwagger);

            rootStore.qnaProjectStoreHouse.add(projectStore);

            tracker.setState('completed');
        } catch (e) {
            tracker.setState('failed', <Error>e);
        }
    };

    const exportQnaMakerProject = async ({
        projectName,
        fileType,
        context
    }: HandlerParams<{ projectName: string; fileType: Extract<FileType, 'excel' | 'tsv'> }>) => {
        await getQnaMakerProjectService({ context }).exportProject({ projectName, fileType });
    };

    const importQnaMakerProject = async ({
        projectName,
        fileType,
        file,
        context
    }: HandlerParams<{ projectName: string; file: File; fileType: Extract<FileType, 'excel' | 'tsv'> }>) => {
        let fileUri: string;
        try {
            fileUri = await getQnaMakerCommonService({ context }).uploadFiles({
                file
            });
        } catch (error) {
            // Error with MI upload, try SAS upload
            console.error('Failed to upload file using MI:', error);
            try {
                const sasUri = await getQnaMakerCommonService({ context }).getSASUri();

                fileUri = await getQnaMakerCommonService({ context }).uploadFilesWithKey({
                    file,
                    sasToken: sasUri.token,
                    sasEndpoint: sasUri.endpoint
                });
            } catch (keyError) {
                console.error('Failed to upload file using key or MI', keyError);
                // Both upload methods failed
                throw keyError;
            }
        }
        await getQnaMakerProjectService({ context }).importProject({ projectName, fileUri, fileType });
        await getQnaMakerProjects({ context: { forceRefresh: true } });
    };

    const deleteQnaMakerProjects = async ({
        projectIds,
        appsLocalStoreHouse,
        context
    }: HandlerParams<{ projectIds: string[]; appsLocalStoreHouse?: ResourceStoreHouse<AppStore> }>) => {
        const { qnaProjectStoreHouse } = rootStore;
        const storeHouse = appsLocalStoreHouse || qnaProjectStoreHouse;

        const projectNames = storeHouse.items.filter(project => projectIds.includes(project.id)).map(project => project.name);
        const servicePromises = projectNames.map(projectName => getQnaMakerProjectService({ context }).deleteProject({ projectName }));

        const resolvedPromises = await batchPromises(servicePromises);
        const idsToDelete = projectIds.filter((_, index) => resolvedPromises.results[index].status === 'success');

        storeHouse.batchRemoveByIds(idsToDelete);
    };

    const openQnaMakerProject = async ({
        projectId,
        csAccountPath,
        handleDifferentResources,
        context
    }: HandlerParams<{
        projectId: string;
        csAccountPath: string;
        handleDifferentResources: () => void;
    }>) => {
        if (csAccountPath && csAccountPath !== rootStore.azureStore.session.csAccountStore?.path) {
            handleDifferentResources();
            throw Error;
        }

        let projectStore = rootStore.qnaProjectStoreHouse.findById(projectId);

        if (!projectStore) {
            projectStore = await getQnaMakerProjectService({ context })
                .getProject({ projectId })
                .then(qnaMakerProjectSwaggerToStore);
        }

        projectStore.setCsAccountPath(rootStore.azureStore.session.csAccountStore?.path);
        rootStore.setCurrentQnaProjectStore(projectStore);
        rootStore.qnaProjectStoreHouse.setItems([projectStore]);
    };

    const closeQnaMakerProject = async () => {
        rootStore.setCurrentQnaProjectStore(null);
    };

    const deployQnAMakerProject = async ({ projectId, context }: HandlerParams<{ projectId: string }>) => {
        const projectStore = rootStore.qnaProjectStoreHouse.findById(projectId);

        await getQnaMakerProjectService({ context }).deployProject({
            projectName: projectId,
            operationHandle: {
                retryStrategy: res => res.data.status !== 'succeeded' && res.data.status !== 'failed',
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                handleCall: data => {
                    projectStore.setPublishStatus(data.status);

                    if (data.status === 'succeeded') {
                        projectStore.setLastPublishedDate(data.lastUpdatedDateTime ? new Date(data.lastUpdatedDateTime) : new Date());
                    } else if (data.status === 'failed') {
                        throw new Error(data.errors[0]?.message);
                    }
                }
            }
        });
    };

    const updateQnaMakerProject = async (params?: HandlerParams<{ projectMetaData: QnaMakerProjectDto }>) => {
        const { context, projectMetaData } = params;

        const projectId: string = projectMetaData.name;
        const projectStore = rootStore.qnaProjectStoreHouse.findById(projectId);

        await getQnaMakerProjectService({ context }).createOrUpdateProject({
            payload: qnaMakerProjectDtoToPayloadSwagger(projectMetaData)
        });

        transaction(() => {
            projectStore.setDescription(projectMetaData.description);
            projectStore.setDefaultAnswer(projectMetaData.defaultAnswer);
        });
    };

    const predictQnA = async ({
        context,
        metadata,
        question,
        projectName,
        useDeployedQnA,
        logicalOperation,
        includeShortAnswer,
        confidenceScoreThreshold,
        previousQnAId,
        previousUserQuery,
        qnaId
    }: HandlerParams<{
        question: string;
        projectName: string;
        useDeployedQnA: boolean;
        includeShortAnswer: boolean;
        metadata: QnaMakerMetadataType;
        confidenceScoreThreshold: number;
        logicalOperation: LogicalOperation;
        previousQnAId: number;
        previousUserQuery: string;
        qnaId?: number;
    }>) => {
        const projectStore = rootStore.qnaProjectStoreHouse.findById(projectName);

        const predictions = await getQnaMakerProjectService({ context }).predict({
            question,
            metadata,
            projectName,
            useDeployedQnA,
            logicalOperation,
            includeShortAnswer,
            confidenceScoreThreshold,
            previousQnAId,
            previousUserQuery,
            qnaId
        });

        const sortedAnswers = predictions.answers.sort((a, b) => (a.confidenceScore < b.confidenceScore ? 1 : -1));
        const testPanePredictionItems: QnaMakerPredictedQnA = {
            question,
            id: getRandomNumber(),
            answers: sortedAnswers.map(answer => ({
                id: answer.id,
                text: answer.answer,
                source: answer.source,
                questions: answer.questions,
                score: answer.confidenceScore,
                prompts: answer.dialog?.prompts,
                answerSpan: answer.answerSpan && { score: answer.answerSpan.confidenceScore, text: answer.answerSpan.text }
            }))
        };
        projectStore.testPanePredictionStoreHouse.add(testPanePredictionItems, 'back');
    };

    const clearTestQnAs = async ({ projectName }: HandlerParams<{ projectName: string }>) => {
        const projectStore = rootStore.qnaProjectStoreHouse.findById(projectName);

        projectStore.testPanePredictionStoreHouse.setItems([]);
    };

    return {
        predictQnA,
        clearTestQnAs,
        getQnaMakerProjects,
        openQnaMakerProject,
        closeQnaMakerProject,
        createQnaMakerProject,
        exportQnaMakerProject,
        importQnaMakerProject,
        deployQnAMakerProject,
        updateQnaMakerProject,
        deleteQnaMakerProjects
    };
};
